import React from 'react';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, XCircle } from 'lucide-react';
import { Pill } from './Pill';

// Shared by the QuickBooks area's Products tab and the Product page
// (issue #265): picking a QuickBooks Item, the state of the connection,
// and a linked Item's status.

const SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    fontSize: 14,
    borderColor: state.isFocused ? '#1976d2' : '#bfc8d6',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(25, 118, 210, 0.15)' : 'none',
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  indicatorsContainer: (base) => ({ ...base, height: 30 }),
  option: (base) => ({ ...base, fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

export function itemLabel(item) {
  if (!item) return '';
  return item.sku ? `${item.full_name || item.name} · ${item.sku}` : (item.full_name || item.name);
}

// Active Inventory Items not yet linked to another Product, searchable by
// name or SKU. `items` comes from /api/quickbooks/products(/items).
export function QuickBooksItemPicker({ items, value, onChange, disabled, autoFocus, placeholder = 'Search QuickBooks Items...' }) {
  const options = items.map((item) => ({ value: item.id, label: itemLabel(item), item }));
  return (
    <Select
      value={options.find((opt) => opt.value === value) || null}
      onChange={(opt) => onChange(opt ? opt.item : null)}
      options={options}
      isDisabled={disabled}
      isSearchable
      autoFocus={autoFocus}
      placeholder={placeholder}
      noOptionsMessage={() => 'No unlinked Inventory Items match'}
      styles={SELECT_STYLES}
      menuPortalTarget={document.body}
    />
  );
}

const NOTICE_TEXT = {
  not_connected: 'QuickBooks isn’t connected, so matches can’t be changed. Existing matches are shown as they were last checked.',
  needs_reconnect: 'The connection to QuickBooks has expired, so matches can’t be changed until it’s reconnected. Existing matches are shown as they were last checked.',
  unreachable: 'QuickBooks couldn’t be reached just now, so matches can’t be changed. Existing matches are shown as they were last checked.',
};

// Shown in place of the picker when Items couldn't be fetched, linking to
// API Integrations where an Admin connects or reconnects QuickBooks.
export function QuickBooksConnectionNotice({ connection, style }) {
  const navigate = useNavigate();
  if (!connection || connection.status === 'connected') return null;
  const temporary = connection.status === 'unreachable';
  const Icon = temporary ? AlertTriangle : XCircle;
  const color = temporary ? '#92400e' : '#b91c1c';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 13, color, ...style }}>
      <Icon size={15} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>
        {NOTICE_TEXT[connection.status] || connection.message}
        {temporary && connection.message ? ` (${connection.message})` : ''}{' '}
        <button
          type="button"
          onClick={() => navigate('/global-api-integrations')}
          style={{ background: 'none', border: 'none', color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13, padding: 0 }}
        >
          Go to API Integrations
        </button>
      </span>
    </div>
  );
}

const STATUS_PILLS = {
  inactive: { label: 'Inactive in QuickBooks', title: 'This Item has been made inactive (deleted) in QuickBooks. Reactivate it there, or link this Product to another Item.' },
  missing: { label: 'Not found in QuickBooks', title: 'This Item is no longer in QuickBooks as an Inventory Item. Link this Product to another Item.' },
};

// Warning pill for a match whose QuickBooks Item is inactive or gone;
// nothing for a healthy match.
export function QuickBooksItemStatusPill({ status }) {
  const pill = STATUS_PILLS[status];
  if (!pill) return null;
  return <Pill color="#b91c1c" background="#fef2f2" title={pill.title}>{pill.label}</Pill>;
}
